import React, { useRef } from 'react';
import cx from 'classnames';
import {
  PushButton,
  HoldButton,
} from '..';
import { observer } from 'mobx-react';
import './LockableButtonGroup.css';
import { useLockableButtons } from './useLockableButtons';

export type ButtonName = 'up' | 'down';

interface ButtonState {
  active: boolean;
  locked: boolean;
  wasLocked: boolean;
}

interface ButtonStates {
  up: ButtonState;
  down: ButtonState;
}

export interface LockableButtonGroupState {
  lockModeEnabled: boolean;
  activeButton: ButtonName | null;
  holdButtonPressed: boolean;
  buttonStates: ButtonStates;
}

export interface LockableButtonGroupProps {
  id: string;
  upButtonPort?: string;
  downButtonPort?: string;
  hideHoldButton?: boolean;
  className?: string;
  upLimitReached?: boolean;
  downLimitReached?: boolean;
  downButtonDisabled?: boolean;
  onLockStateChange?: (lockModeEnabled: boolean, toggleLockMode: (value: boolean) => void) => void;
}

// Default state for buttons
export const DEFAULT_BUTTON_STATE: ButtonState = { active: false, locked: false, wasLocked: false };

// Default state for the component
export const getInitialState = (): LockableButtonGroupState => ({
  lockModeEnabled: false,
  activeButton: null,
  holdButtonPressed: false,
  buttonStates: {
    up: { ...DEFAULT_BUTTON_STATE },
    down: { ...DEFAULT_BUTTON_STATE },
  },
});

const LockableButtonGroup: React.FC<LockableButtonGroupProps> = (props) => {
  const {
    hideHoldButton = false,
    className = '',
    onLockStateChange,
  } = props;

  const upButtonRef = useRef<PushButton>(null);
  const downButtonRef = useRef<PushButton>(null);

  const {
    state,
    toggleLockMode,
    handleButtonPress,
    handleButtonRelease,
    handleHoldPress,
    handleHoldRelease,
  } = useLockableButtons(props);

  const { lockModeEnabled, buttonStates, holdButtonPressed } = state;

  React.useEffect(() => {
    if (onLockStateChange) {
      onLockStateChange(lockModeEnabled, toggleLockMode);
    }
  }, [lockModeEnabled, toggleLockMode, onLockStateChange]);

  const lockModeControlGroupClassName = cx('lock-mode-control-group', {
    'locked-red': buttonStates.up.locked && !holdButtonPressed,
    'locked-green': buttonStates.down.locked && !holdButtonPressed,
    'locked-red-held': buttonStates.up.locked && holdButtonPressed,
    'locked-green-held': buttonStates.down.locked && holdButtonPressed,
    'locked-held': holdButtonPressed,
  }, className);

  return (
    <div className={lockModeControlGroupClassName}>
      <div className="button-group">
        <PushButton
          ref={upButtonRef}
          className={'btn-batch btn-batch-red'}
          port={props.upButtonPort}
          active={buttonStates.up.active}
          onPush={() => handleButtonPress('up')}
          onPushUp={() => handleButtonRelease('up')}
          setOutputOnRelease={false}
          disabled={holdButtonPressed || props.upLimitReached}
        >
          <i className={'fa fa-arrow-up'} />
        </PushButton>
        <PushButton
          ref={downButtonRef}
          className={'btn-batch btn-batch-green'}
          port={props.downButtonPort}
          active={buttonStates.down.active}
          onPush={() => handleButtonPress('down')}
          onPushUp={() => handleButtonRelease('down')}
          setOutputOnRelease={false}
          disabled={holdButtonPressed || props.downLimitReached || props.downButtonDisabled}
        >
          <i className={'fa fa-arrow-down'} />
        </PushButton>
      </div>

      {lockModeEnabled && !hideHoldButton && (
        <HoldButton
          disabled={!state.activeButton}
          onPush={handleHoldPress}
          onPushUp={handleHoldRelease}>
          HOLD
        </HoldButton>
      )}
    </div>
  );
};

export default observer(LockableButtonGroup);
